import React, { useMemo, useState } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import { Box, CircularProgress, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import color from "../../Constants/colors";
import MarkdownCode from "./MarkdownCode";
import DiagramBlock from "./DiagramBlock";
import call from "../../Api/call";
import { downloadDocumentBlob } from "../../Helpers/documentHelper";
import { getFileTypeStyle } from "../../Helpers/fileTypePresentation";

interface ChatMarkdownProps {
  content: string;
  conversationId?: number;
}

const DOC_TOKEN_PATTERN = /^doc:(\d+)$/;
const SAFE_URI_PATTERN = /^(?:https?:|mailto:|tel:|doc:)/i;

const transformLinkUri = (uri: string): string => {
  const trimmed = (uri ?? "").trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("/") || trimmed.startsWith("#")) return uri;
  return SAFE_URI_PATTERN.test(trimmed) ? uri : "";
};

const extractDocumentId = (href: string | undefined): number | null => {
  if (!href) return null;
  const match = href.trim().match(DOC_TOKEN_PATTERN);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const filenameFromMarkdownChildren = (children: React.ReactNode): string => {
  if (typeof children === "string") return children;
  if (
    Array.isArray(children) &&
    children.length > 0 &&
    typeof children[0] === "string"
  ) {
    return children[0] as string;
  }
  return "document";
};

const ChatMarkdownDocumentLink: React.FC<{
  href: string;
  conversationId: number;
  children: React.ReactNode;
}> = ({ href, conversationId, children }) => {
  const [downloading, setDownloading] = useState(false);
  const documentId = extractDocumentId(href);
  const filename = filenameFromMarkdownChildren(children);
  const { Icon: DocIcon, iconColor, iconBg } = getFileTypeStyle(filename);

  if (documentId === null) return null;

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const path = `/v1/agent_chat/${conversationId}/documents/${documentId}/download/`;
      const response = await call.get(path, { responseType: "blob" });
      downloadDocumentBlob(response, filename);
    } catch (err) {
      console.error("Failed to download generated document", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      component="button"
      type="button"
      disabled={downloading}
      onClick={handleDownload}
      aria-label={filename}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        maxWidth: "100%",
        py: 0.6,
        pl: 0.6,
        pr: 1.1,
        my: 0.5,
        border: `1px solid ${color.gray200}`,
        borderLeft: `3px solid ${iconColor}`,
        borderRadius: "12px",
        bgcolor: color.white,
        cursor: downloading ? "wait" : "pointer",
        font: "inherit",
        textAlign: "left",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        transition: "border-color 0.15s, box-shadow 0.15s, transform 0.12s",
        opacity: downloading ? 0.85 : 1,
        "&:hover:not(:disabled)": {
          borderColor: iconColor,
          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          transform: "translateY(-1px)",
        },
        "&:disabled": { cursor: "wait" },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "9px",
          bgcolor: iconBg,
          color: iconColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {downloading ? (
          <CircularProgress size={18} sx={{ color: iconColor }} />
        ) : (
          <DocIcon sx={{ fontSize: 18 }} />
        )}
      </Box>
      <Typography
        component="span"
        variant="body2"
        sx={{
          color: color.primary_dark,
          fontWeight: 600,
          fontSize: "0.88rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
          flex: 1,
        }}
      >
        {children}
      </Typography>
      <DownloadIcon
        sx={{
          fontSize: 18,
          color: downloading ? color.gray400 : color.gray500,
          flexShrink: 0,
        }}
        aria-hidden
      />
    </Box>
  );
};

const ChatMarkdown: React.FC<ChatMarkdownProps> = ({
  content,
  conversationId,
}) => {
  const components = useMemo<Components>(
    () => ({
      p: ({ ...props }) => (
        <Typography
          variant="body2"
          component="p"
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            fontSize: "0.9rem",
            color: color.black,
            fontWeight: 500,
            margin: "0.5rem 0",
            "&:first-of-type": { marginTop: 0 },
            "&:last-of-type": { marginBottom: 0 },
          }}
          {...props}
        />
      ),
      strong: ({ ...props }) => (
        <Typography
          component="strong"
          variant="body2"
          sx={{
            fontWeight: "bold",
            fontSize: "0.9rem",
            color: color.black,
          }}
          {...props}
        />
      ),
      em: ({ ...props }) => (
        <Typography
          component="em"
          variant="body2"
          sx={{
            fontStyle: "italic",
            fontSize: "0.9rem",
            color: color.black,
          }}
          {...props}
        />
      ),
      ul: ({ ...props }) => (
        <Box
          component="ul"
          sx={{
            margin: "0.5rem 0",
            paddingLeft: "1.5rem",
            fontSize: "0.9rem",
          }}
          {...props}
        />
      ),
      ol: ({ ...props }) => (
        <Box
          component="ol"
          sx={{
            margin: "0.5rem 0",
            paddingLeft: "1.5rem",
            fontSize: "0.9rem",
          }}
          {...props}
        />
      ),
      li: ({ ...props }) => (
        <Typography
          component="li"
          variant="body2"
          sx={{
            wordBreak: "break-word",
            overflowWrap: "break-word",
            fontSize: "0.9rem",
            color: color.black,
            lineHeight: "1.5",
            margin: "0.25rem 0",
          }}
          {...props}
        />
      ),
      h1: ({ ...props }) => (
        <Typography
          variant="h6"
          component="h1"
          sx={{
            fontSize: "1.1rem",
            fontWeight: "bold",
            color: color.black,
            margin: "0.75rem 0 0.5rem 0",
          }}
          {...props}
        />
      ),
      h2: ({ ...props }) => (
        <Typography
          variant="subtitle1"
          component="h2"
          sx={{
            fontSize: "1rem",
            fontWeight: "bold",
            color: color.black,
            margin: "0.75rem 0 0.5rem 0",
          }}
          {...props}
        />
      ),
      h3: ({ ...props }) => (
        <Typography
          variant="subtitle2"
          component="h3"
          sx={{
            fontSize: "0.95rem",
            fontWeight: "bold",
            color: color.black,
            margin: "0.75rem 0 0.5rem 0",
          }}
          {...props}
        />
      ),
      blockquote: ({ ...props }) => (
        <Box
          component="blockquote"
          sx={{
            borderLeft: `3px solid ${color.primary_light}`,
            paddingLeft: "1rem",
            margin: "0.5rem 0",
            fontStyle: "italic",
            color: color.primary_dark,
          }}
          {...props}
        />
      ),
      pre: ({ ...props }) => (
        <Box
          component="pre"
          sx={{
            width: "100%",
            margin: "0.5rem 0",
          }}
          {...props}
        />
      ),
      code: (props) => {
        const { inline, className, children } = props as {
          inline?: boolean;
          className?: string;
          children?: React.ReactNode;
        };
        // Render an ```svg fenced block as a downloadable diagram image instead
        // of raw code (the agent still keeps the SVG in its message history).
        if (!inline && /(^|\s)language-svg(\s|$)/.test(className || "")) {
          const svgText = Array.isArray(children)
            ? children.join("")
            : String(children ?? "");
          if (svgText.includes("<svg")) {
            return <DiagramBlock svg={svgText.trim()} />;
          }
        }
        return <MarkdownCode {...props} />;
      },
      a: ({ href, children, ...props }) => {
        const documentId = extractDocumentId(href);
        if (documentId !== null && conversationId) {
          return (
            <ChatMarkdownDocumentLink
              href={href!}
              conversationId={conversationId}
            >
              {children}
            </ChatMarkdownDocumentLink>
          );
        }
        return (
          <Typography
            component="a"
            variant="body2"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: color.primary_dark,
              textDecoration: "underline",
              fontSize: "0.9rem",
            }}
            {...props}
          >
            {children}
          </Typography>
        );
      },
    }),
    [conversationId],
  );

  return (
    <ReactMarkdown transformLinkUri={transformLinkUri} components={components}>
      {content}
    </ReactMarkdown>
  );
};

export default React.memo(ChatMarkdown);
